import { teamAbbr } from '../../teams'
import type { FeedEventView } from '../../adminApi'

/**
 * Turning a feed row into something that reads as football.
 *
 * A goal renders as a score — `ARS 2–1 CHE` — and a minute as a minute, using the same team
 * registry the rest of the app does, so the feed reads as football and not as a log line. Its own
 * module because the toast, the feed panel and the match cards all need the same strings and must
 * not each invent their own.
 */

/** Events an operator must not lose to an auto-dismiss: an error nobody saw is not an error told. */
export const STICKY_TYPES = new Set(['sync-failed', 'budget-paused', 'status-unknown'])

/** Per-type dwell. `goal` and `fulltime` linger; the rest go at the app's usual pace. */
export function dwellMs(type: string): number | null {
  if (STICKY_TYPES.has(type)) return null
  if (type === 'goal' || type === 'fulltime') return 8_000
  return 5_000
}

export type ToastTone = 'goal' | 'fulltime' | 'error' | 'plain'

export function toneFor(type: string): ToastTone {
  if (type === 'goal') return 'goal'
  if (type === 'fulltime') return 'fulltime'
  if (STICKY_TYPES.has(type) || type === 'abandoned' || type === 'cancelled') return 'error'
  return 'plain'
}

export function iconFor(type: string): string {
  switch (type) {
    case 'goal':
      return '⚽'
    case 'goal-disallowed':
      return '🚫'
    case 'kickoff':
      return '▶'
    case 'halftime':
      return '⏸'
    case 'second-half':
      return '▶'
    case 'fulltime':
      return '⏹'
    case 'postponed':
    case 'cancelled':
    case 'abandoned':
      return '⚠'
    case 'status-unknown':
      return '❓'
    case 'rescheduled':
      return '📅'
    case 'score-corrected':
      return '✎'
    case 'sync-failed':
      return '✖'
    case 'budget-paused':
      return '⏸'
    case 'budget-resumed':
      return '▶'
    case 'deadline-passed':
      return '🔒'
    case 'gameweek-advanced':
      return '⏭'
    case 'admin-action':
      return '👤'
    default:
      return '•'
  }
}

/** `ARS 2–1 CHE`, or null when the event carries no scoreline. */
export function scoreLine(event: FeedEventView): string | null {
  if (event.homeScore === null || event.awayScore === null) return null
  if (!event.homeTeamId || !event.awayTeamId) {
    return `${event.homeScore}–${event.awayScore}`
  }
  return `${teamAbbr(event.homeTeamId)} ${event.homeScore}–${event.awayScore} ${teamAbbr(event.awayTeamId)}`
}

/** The headline: what happened, in the fewest words that stay unambiguous. */
export function titleFor(event: FeedEventView): string {
  const meta = event.meta ?? {}
  switch (event.type) {
    case 'goal': {
      const scorer = typeof meta.scorer === 'string' ? meta.scorer : null
      const team = event.teamId ? teamAbbr(event.teamId) : 'Goal'
      return scorer ? `Goal — ${scorer} (${team})` : `Goal — ${team}`
    }
    case 'goal-disallowed':
      return `Goal disallowed — ${event.teamId ? teamAbbr(event.teamId) : 'VAR'}`
    case 'kickoff':
      return 'Kick-off'
    case 'halftime':
      return 'Half time'
    case 'second-half':
      return 'Second half'
    case 'fulltime':
      return 'Full time'
    case 'postponed':
      return 'Postponed'
    case 'cancelled':
      return 'Cancelled'
    case 'abandoned':
      return 'Abandoned'
    case 'rescheduled':
      return 'Rescheduled'
    case 'score-corrected':
      return 'Score corrected'
    // Loud on purpose: `unknown` means a provider status we do not map, which is a bug that is
    // otherwise invisible until a gameweek refuses to finalize.
    case 'status-unknown':
      return 'Unknown status — a provider code we do not map'
    case 'sync-failed':
      return `Sync failed — ${typeof meta.resource === 'string' ? meta.resource : 'unknown resource'}`
    case 'budget-paused':
      return 'Syncing paused'
    case 'budget-resumed':
      return 'Syncing resumed'
    // The auto-fill runs at this exact moment, so its size belongs on this line
    // rather than in an event of its own: how many predictions the deadline had to invent is the
    // first thing an operator asks when a lock looks like it fired at the wrong time. Older events
    // carry no count and read exactly as they did.
    case 'deadline-passed': {
      const gameweek = `GW${typeof meta.gameweek === 'number' ? meta.gameweek : '?'}`
      const autoFilled = typeof meta.autoFilled === 'number' ? meta.autoFilled : 0
      return autoFilled > 0
        ? `Deadline passed — ${gameweek} · ${autoFilled} prediction${
            autoFilled === 1 ? '' : 's'
          } auto-filled`
        : `Deadline passed — ${gameweek}`
    }
    // Three hours after a gameweek's last match finished, everyone moved on. Worth a
    // line in the feed: it is the one moment the whole user base changes gameweek at once.
    case 'gameweek-advanced':
      return `Gameweek advanced — GW${typeof meta.gameweek === 'number' ? meta.gameweek : '?'} → GW${
        typeof meta.to === 'number' ? meta.to : '?'
      }`
    case 'admin-action':
      return `Admin — ${typeof meta.action === 'string' ? meta.action : 'action'}`
    default:
      return event.type
  }
}

/** The second line: the score, the minute, the error text — whatever adds to the headline. */
export function detailFor(event: FeedEventView): string | null {
  const meta = event.meta ?? {}
  if (event.scope === 'system') {
    if (typeof meta.error === 'string') return meta.error
    if (typeof meta.reason === 'string') return meta.reason
    if (typeof meta.moved === 'number') {
      return `${meta.moved} ${meta.moved === 1 ? 'player' : 'players'} moved`
    }
    return null
  }
  const parts: string[] = []
  const score = scoreLine(event)
  if (score) parts.push(score)
  // `halftime` shows "HT", never a running number — there is no minute to show at half time.
  if (event.type === 'halftime') parts.push('HT')
  else if (event.minute !== null) parts.push(`${event.minute}'`)
  return parts.length > 0 ? parts.join(' · ') : null
}

/** "2m ago" — the absolute instant is on the title attribute for anyone who wants it. */
export function relativeTime(iso: string, nowMs: number): string {
  const seconds = Math.max(0, Math.round((nowMs - Date.parse(iso)) / 1000))
  if (seconds < 60) return `${seconds}s ago`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`
  return `${Math.floor(seconds / 86_400)}d ago`
}
